// App.jsx
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import TopBar from "./components/layout/TopBar";
import Footer from "./components/layout/Footer";
import MobileBottomNav from "./components/layout/MobileBottomNav";
import AppRoutes from "./routes/AppRoutes";
import MaintenancePage from "./pages/static/MaintenancePage";
import AppErrorBoundary from "./components/common/AppErrorBoundary";

import AdminAuthProvider from "./contexts/AdminAuthContext";
import SuperAdminAuthProvider from "./contexts/SuperAdminAuthContext";
import UserAuthProvider from "./contexts/UserAuthContext";
import { MobileNavProvider } from "./contexts/MobileNavContext";
import { TopBarSlotsProvider } from "./contexts/TopBarSlotsContext";
import { ToastProvider } from "./contexts/ToastContext";
import { ComparisonProvider } from "./contexts/ComparisonContext";
import { FavoritesProvider } from "./contexts/FavoritesContext";
import { SocketProvider } from "./contexts/SocketContext";

import AutoScrollToTop from "./utils/AutoScrollToTop";
import ScrollToTopButton from "./utils/scrollToTopButton";
import { getLayoutChrome } from "./utils/layoutChrome";
import ShoppingProfileOnboardGate from "./components/onboarding/ShoppingProfileOnboardGate";
import FloatingComparisonBar from "./components/product/FloatingComparisonBar";
import ApiUnreachableBanner from "./components/common/ApiUnreachableBanner";
import { PublicPagesProvider, usePublicPages } from "./contexts/PublicPagesContext";

// Create a client with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const readMobileViewportMatch = () => {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia?.("(max-width: 768px)");
  return Boolean(mq?.matches);
};

const Layout = () => {
  const location = useLocation();
  // Server-driven: sourced from GET /api/settings/public (see
  // PublicPagesContext), which reads AdminSettings.system.maintenanceMode.
  // This ensures every visitor's browser reflects the admin's actual saved
  // setting, not just the admin's own tab (the previous localStorage-only
  // implementation never reached other users).
  const { isPageEnabled, maintenanceMode } = usePublicPages();
  const isAdminRoute =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/superadmin");
  const showMaintenance = maintenanceMode && !isAdminRoute;
  const layoutChrome = getLayoutChrome(location.pathname);
  const hideChrome =
    isAdminRoute || showMaintenance || !layoutChrome.showTopBar;

  const [isMobileViewport, setIsMobileViewport] = useState(readMobileViewportMatch);

  const showMobileBottomNav =
    !hideChrome && layoutChrome.showMobileBottomNav;

  const showMobileAppShell = showMobileBottomNav && isMobileViewport;

  const topBarVariant = showMobileAppShell
    ? "compact"
    : layoutChrome.topBarVariant;

  const showFooter =
    !hideChrome && layoutChrome.showFooter && !showMobileAppShell;

  useEffect(() => {
    const mq = window.matchMedia?.("(max-width: 768px)");
    if (!mq) return undefined;

    const syncViewportClass = () => {
      setIsMobileViewport(mq.matches);
      document.body.classList.toggle("mobile-viewport", mq.matches);
    };
    syncViewportClass();
    mq.addEventListener("change", syncViewportClass);
    return () => mq.removeEventListener("change", syncViewportClass);
  }, []);

  useEffect(() => {
    if (showMobileAppShell) {
      document.body.classList.add("mobile-bottom-nav-active");
    } else {
      document.body.classList.remove("mobile-bottom-nav-active");
    }
    return () => document.body.classList.remove("mobile-bottom-nav-active");
  }, [showMobileAppShell]);

  if (showMaintenance) {
    return (
      <>
        <AutoScrollToTop />
        <MaintenancePage />
      </>
    );
  }

  return (
    <>
      {/* Titles are brand-suffixed in PageSeo (Helmet titleTemplate is unreliable on React 19). */}
      <Helmet defaultTitle="Simpary" />
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>
      {!hideChrome && <TopBar variant={topBarVariant} />}
      <AutoScrollToTop />
      <ScrollToTopButton />
      <div id="main-content" tabIndex={-1}>
        <ApiUnreachableBanner />
        <ShoppingProfileOnboardGate>
          <Routes>
            <Route path="/*" element={<AppRoutes />} />
          </Routes>
        </ShoppingProfileOnboardGate>
      </div>
      {!hideChrome && showFooter && <Footer />}
      {showMobileAppShell && <MobileBottomNav />}
      {!isAdminRoute && isPageEnabled("compare") && <FloatingComparisonBar />}
    </>
  );
};

function AppShell() {
  return (
    <PublicPagesProvider>
      <MobileNavProvider>
        <TopBarSlotsProvider>
          <Layout />
        </TopBarSlotsProvider>
      </MobileNavProvider>
    </PublicPagesProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <Router>
            <UserAuthProvider>
              <FavoritesProvider>
                <SocketProvider>
                  <AdminAuthProvider>
                    <SuperAdminAuthProvider>
                      <ComparisonProvider>
                        <AppErrorBoundary>
                          <AppShell />
                        </AppErrorBoundary>
                      </ComparisonProvider>
                    </SuperAdminAuthProvider>
                  </AdminAuthProvider>
                </SocketProvider>
              </FavoritesProvider>
            </UserAuthProvider>
          </Router>
        </ToastProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
